/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// actions
import { getListing } from "../../actions";
// BASE Components
import BASEPageSimple from "../../../@BASE/core/BASEPageSimple";
import BASELoading from "../../../@BASE/core/BASELoading";
import withReducer from "../../store/withReducer";
import useThemeMediaQuery from "../../../@BASE/hooks/useThemeMediaQuery";
// full calendar components
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
// mui components
import { styled, useTheme } from '@mui/material/styles';
// custom components
import ReviewHeader from "./ReviewHeader";
import ReviewsDialog from "./dialogs/ReviewsDialog";
// store
import reducer from './store';
import {
  getReviews,
  openEditReviewDialog,
  openNewReviewDialog,
  selectFilteredReviews,
  updateReview
} from './store/reviewsSlice';
import ReviewSidebar from "./ReviewSidebar";
import ReviewContent from "./ReviewContent";
import { selectCycles } from "./store/cyclesSlice";

const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& a': {
    color: `${theme.palette.text.primary}!important`,
    textDecoration: 'none!important',
  },
  '&  .fc-media-screen': {
    minHeight: '100%',
    width: '100%',
  },
  '& .fc-scrollgrid, & .fc-theme-standard td, & .fc-theme-standard th': {
    borderColor: `${theme.palette.divider}!important`,
  },
  '&  .fc-scrollgrid-section > td': {
    border: 0,
  },
  '& .fc-daygrid-day': {
    '&:last-child': {
      borderRight: 0,
    },
  },
  '& .fc-col-header-cell': {
    borderWidth: '0 1px 0 1px',
    padding: '8px 0 0 0',
    '& .fc-col-header-cell-cushion': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      fontSize: 12,
      textTransform: 'uppercase',
    },
  },
  '& .fc-view ': {
    '& > .fc-scrollgrid': {
      border: 0,
    },
  },
  '& .fc-daygrid-day.fc-day-today': {
    backgroundColor: 'transparent!important',
    '& .fc-daygrid-day-number': {
      borderRadius: '100%',
      backgroundColor: `${theme.palette.secondary.main}!important`,
      color: `${theme.palette.secondary.contrastText}!important`,
    },
  },
  '& .fc-daygrid-day-top': {
    justifyContent: 'center',

    '& .fc-daygrid-day-number': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      fontSize: 12,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      margin: '4px 0',
      borderRadius: '50%',
      float: 'none',
      lineHeight: 1,
    },
  },
  '& .fc-h-event': {
    background: 'initial',
  },
  '& .fc-event': {
    border: 0,
    padding: '0 ',
    fontSize: 12,
    margin: '0 6px 4px 6px!important',
  },
}));

function Reviews(props) {
  const dispatch = useDispatch();
  // const [courses, setCourses] = useState(null);
  const [lessons , setLessons] = useState(null);
  const reviews = useSelector(selectFilteredReviews);
  const [currentDate, setCurrentDate] = useState(null);
  
  
  
  const calendarRef = useRef();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(!isMobile);
  const theme = useTheme();
  const cycles = useSelector(selectCycles);
  
  // fetch lessons
  const getLessons = async () => {
    try {
      const response = await dispatch(getListing('lessons')()).unwrap();
      
      // map response to have label and id
      const miniLessons = response.map((lesson) => ({
        id: lesson.id,
        label: lesson.label,
        classification: lesson.classification,
        title: lesson.title,
      }));

      setLessons(miniLessons);
    } catch (err) {
      console.log(err);
    }
  }; 
  // get data from API on page load
  useEffect(() => {
    getLessons();
    dispatch(getReviews());
  }, [dispatch]);

  useEffect(() => {
    setLeftSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    // Correct calendar dimentions after sidebar toggles
    setTimeout(() => {
      calendarRef.current?.getApi()?.updateSize();
    }, 300);
  }, [leftSidebarOpen]);

  const handleDateSelect = (selectInfo) => {
    const { start, end } = selectInfo;
    dispatch(openNewReviewDialog(selectInfo));
  };

  const handleReviewDrop = (eventDropInfo) => {
    const { id, lessons, cycle, start, end } = eventDropInfo.event;
    dispatch(
      updateReview({
        id,
        lessons,
        cycle,
        start,
        end,
      })
    );
  };

  const handleReviewClick = (clickInfo) => {
    dispatch(openEditReviewDialog(clickInfo));
  };

  const handleDates = (rangeInfo) => {
    setCurrentDate(rangeInfo);
  }

  const handleReviewAdd = (addInfo) => {};

  const handleReviewChange = (changeInfo) => {};

  const handleReviewRemove = (removeInfo) => {};

  const handleToggleLeftSidebar = () => {
    setLeftSidebarOpen(!leftSidebarOpen);
  }

  if (!reviews || !lessons) {
    return <BASELoading />;
  }

  console.log('reviews', reviews);

  return (
    <>
      <Root
        header={
          <ReviewHeader
            calendarRef={calendarRef}
            currentDate={currentDate}
            onToggleLeftSidebar={handleToggleLeftSidebar}
          />
        }
        content={
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={false}
            initialView="dayGridMonth"
            editable
            selectable
            selectMirror
            dayMaxEvents
            weekends
            datesSet={handleDates}
            select={handleDateSelect}
            events={reviews}
            eventContent={(eventInfo) => <ReviewContent eventInfo={eventInfo} lessons={lessons} />}
            eventClick={handleReviewClick}
            eventAdd={handleReviewAdd}
            eventChange={handleReviewChange}
            eventRemove={handleReviewRemove}
            eventDrop={handleReviewDrop}
            initialDate={new Date()}
            ref={calendarRef}
          />
        }
        leftSidebarContent={<ReviewSidebar />}
        leftSidebarOpen={leftSidebarOpen}
        leftSidebarOnClose={() => setLeftSidebarOpen(false)}
        leftSidebarWidth={240}
        scroll="content"
      />
      {/* <ReviewsDialog courses={courses} lessons={lessons} /> */}
      <ReviewsDialog lessons={lessons} />
    </>
  );
}

export default withReducer('reviews', reducer)(Reviews);